/**
 * Allys Currency Input Component
 * 
 * Modern currency input with currency selector
 * Following Allys theme styling with Inter font
 */

'use client';

import { CurrencyDollar } from '@phosphor-icons/react';
import { useState } from 'react';

interface Currency {
  code: string;
  name: string;
  symbol: string;
}

const currencies: Currency[] = [
  { code: 'MUR', name: 'Mauritian Rupee', symbol: 'Rs' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
];

export interface AllysCurrencyInputProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  defaultCurrency?: string;
}

export function AllysCurrencyInput({
  id,
  name,
  value = '',
  onChange,
  placeholder = 'e.g., 500,000 - 1,000,000',
  required = false,
  error,
  defaultCurrency = 'MUR',
}: AllysCurrencyInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(() => {
    const found = currencies.find(c => c.code === defaultCurrency);
    return found || currencies[0] || { code: 'MUR', name: 'Mauritian Rupee', symbol: 'Rs' };
  });
  const [amount, setAmount] = useState(value);

  const handleCurrencySelect = (currency: Currency) => {
    setSelectedCurrency(currency);
    setIsOpen(false);
    // Update full value with new currency
    if (onChange) {
      onChange(`${currency.code} ${amount}`);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = e.target.value;
    setAmount(newAmount);
    if (onChange) {
      onChange(`${selectedCurrency.code} ${newAmount}`);
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        {/* Currency Selector */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`
              h-[46px] px-3 flex items-center gap-2
              border ${error ? 'border-red-500' : 'border-gray-200'}
              rounded-lg
              hover:border-gray-900
              focus:border-gray-900 focus:outline-none
              transition-colors duration-200
              bg-white
              font-inter
            `}
          >
            <span className="text-base text-gray-900 font-normal">{selectedCurrency.symbol}</span>
            <span className="text-sm text-gray-600 font-normal">{selectedCurrency.code}</span>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {isOpen && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setIsOpen(false)}
              />
              <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                {currencies.map((currency) => (
                  <button
                    key={currency.code}
                    type="button"
                    onClick={() => handleCurrencySelect(currency)}
                    className={`
                      w-full px-4 py-3 flex items-center gap-3 text-left
                      hover:bg-gray-50 transition-colors duration-150
                      font-inter
                      ${selectedCurrency.code === currency.code ? 'bg-gray-50' : ''}
                    `}
                  >
                    <span className="text-base text-gray-900 font-normal w-8">{currency.symbol}</span>
                    <span className="text-sm text-gray-900 font-normal flex-1">{currency.name}</span>
                    <span className="text-sm text-gray-500 font-normal">{currency.code}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Amount Input */}
        <div className="flex-1 relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            <CurrencyDollar size={20} weight="light" className="text-gray-400" />
          </div>
          <input
            type="text"
            id={id}
            name={name}
            value={amount}
            onChange={handleAmountChange}
            placeholder={placeholder}
            required={required}
            className={`
              w-full pl-11 pr-4 py-3
              border ${error ? 'border-red-500' : 'border-gray-200'}
              rounded-lg
              focus:border-gray-900 focus:outline-none
              transition-colors duration-200
              text-gray-900 placeholder:text-gray-400
              bg-white
              font-inter
            `}
          />
        </div>
      </div>
      
      {error && (
        <p 
          className="mt-2 text-sm text-red-500 font-inter"
        >
          {error}
        </p>
      )}
    </div>
  );
}

